import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, Mail, Lock, User, Store } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = await login(email, password);
      const user = data.user || data;
      // Super admin goes to admin panel, others go to home
      if (user.role === 'super_admin') {
        navigate('/admin');
      } else if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/');
      }
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] flex" data-testid="login-page">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 mb-10">
            <img src="/logo64.png" alt="MorphMX" className="w-10 h-10" />
            <span className="font-semibold text-2xl text-zinc-100">MorphMX</span>
          </Link>

          <h1 className="text-3xl font-light tracking-tight text-zinc-100 mb-2 font-[Outfit]">
            Bienvenido de nuevo
          </h1>
          <p className="text-zinc-500 mb-8">
            Ingresa tus credenciales para acceder a tu cuenta
          </p>

          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-zinc-400 mb-2 block">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-600" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className="pl-10 bg-white/5 border-white/10 text-zinc-100 placeholder-zinc-600 focus:border-purple-500/50"
                  required
                  data-testid="login-email"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-zinc-400 mb-2 block">Contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-600" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 pr-10 bg-white/5 border-white/10 text-zinc-100 placeholder-zinc-600 focus:border-purple-500/50"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="rounded border-white/20 bg-white/5" />
                <span className="text-sm text-zinc-500">Recordarme</span>
              </label>
              <Link to="/forgot-password" className="text-sm text-purple-400 hover:text-purple-300" data-testid="forgot-password-link">
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-zinc-100 text-zinc-900 hover:bg-white h-12"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? 'Ingresando...' : 'Iniciar sesión'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>

          <p className="mt-8 text-center text-zinc-500">
            ¿No tienes cuenta?{' '}
            <Link to="/registro" className="text-purple-400 hover:text-purple-300">
              Regístrate gratis
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block flex-1 relative">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/17830705/pexels-photo-17830705.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0C] to-transparent" />
        <div className="absolute inset-0 bg-[#0A0A0C]/40" />
      </div>
    </div>
  );
}

export function RegisterPage() {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState(searchParams.get('role') || 'buyer');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await register(email, password, name, role);
      navigate(role === 'seller' ? '/dashboard' : '/');
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] flex" data-testid="register-page">
      {/* Left side - Image */}
      <div className="hidden lg:block flex-1 relative">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1643468592814-6fcf455e06e1)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-l from-[#0A0A0C] to-transparent" />
        <div className="absolute inset-0 bg-[#0A0A0C]/40" />
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-10">
            <img src="/logo64.png" alt="MorphMX" className="w-10 h-10" />
            <span className="font-semibold text-2xl text-zinc-100">MorphMX</span>
          </Link>

          <h1 className="text-3xl font-light tracking-tight text-zinc-100 mb-2 font-[Outfit]">
            Crear cuenta
          </h1>
          <p className="text-zinc-500 mb-8">
            Únete a la comunidad de genética animal
          </p>

          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="register-error">
              {error}
            </div>
          )}

          {/* Role Selector */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <button
              type="button"
              onClick={() => setRole('buyer')}
              className={`p-4 rounded-xl border transition-all ${
                role === 'buyer' 
                  ? 'border-purple-500/50 bg-purple-500/10' 
                  : 'border-white/10 bg-white/5 hover:border-white/20'
              }`}
              data-testid="role-buyer"
            >
              <User className={`w-6 h-6 mx-auto mb-2 ${role === 'buyer' ? 'text-purple-400' : 'text-zinc-500'}`} />
              <span className={`text-sm font-medium ${role === 'buyer' ? 'text-zinc-100' : 'text-zinc-400'}`}>
                Comprador
              </span>
            </button>
            <button
              type="button"
              onClick={() => setRole('seller')}
              className={`p-4 rounded-xl border transition-all ${
                role === 'seller' 
                  ? 'border-purple-500/50 bg-purple-500/10' 
                  : 'border-white/10 bg-white/5 hover:border-white/20'
              }`}
              data-testid="role-seller"
            >
              <Store className={`w-6 h-6 mx-auto mb-2 ${role === 'seller' ? 'text-purple-400' : 'text-zinc-500'}`} />
              <span className={`text-sm font-medium ${role === 'seller' ? 'text-zinc-100' : 'text-zinc-400'}`}>
                Vendedor
              </span>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-zinc-400 mb-2 block">Nombre</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-600" />
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Tu nombre"
                  className="pl-10 bg-white/5 border-white/10 text-zinc-100 placeholder-zinc-600"
                  required
                  data-testid="register-name"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-zinc-400 mb-2 block">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-600" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className="pl-10 bg-white/5 border-white/10 text-zinc-100 placeholder-zinc-600"
                  required
                  data-testid="register-email"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-zinc-400 mb-2 block">Contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-600" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="pl-10 pr-10 bg-white/5 border-white/10 text-zinc-100 placeholder-zinc-600"
                  required
                  minLength={6}
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-zinc-100 text-zinc-900 hover:bg-white h-12"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? 'Creando cuenta...' : 'Crear cuenta'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>

          <p className="mt-8 text-center text-zinc-500">
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" className="text-purple-400 hover:text-purple-300">
              Inicia sesión
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
